import { useCallback, useEffect, useMemo, useState } from 'react';
import { Star, ShieldCheck, Camera, X, Award, MessageSquareText } from 'lucide-react';
import { toast } from 'sonner';
import { reputation } from '../lib/api';
import { useAuth } from '../lib/auth';

const KIND_LABEL = {
  product: 'this product',
  store:   'this shop',
  factory: 'this factory',
  agent:   'this agent',
};

/**
 * Universal reputation panel — pluggable into product pages, store profiles,
 * factory profiles, agent profiles.
 *
 * Props:
 *   kind        one of product|store|factory|agent
 *   targetId    id / MS-ID
 *   title       optional heading
 *   showForm    default true — hide when embedded in a summary card
 */
export default function ReputationPanel({ kind, targetId, title, showForm = true }) {
  const { user } = useAuth();
  const [data, setData] = useState({ aggregate: { count: 0, average: 0, breakdown: {} }, reviews: [] });
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!kind || !targetId) return;
    reputation.forTarget(kind, targetId)
      .then(setData)
      .catch(() => setData({ aggregate: { count: 0, average: 0, breakdown: {} }, reviews: [] }));
  }, [kind, targetId]);
  useEffect(load, [load]);

  const submit = async () => {
    if (!user) { toast.error('Sign in to leave a review'); return; }
    setError(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('target_id', targetId);
      fd.append('rating', String(rating));
      if (text.trim()) fd.append('text', text.trim());
      if (photo) fd.append('photo', photo);
      await reputation.create(fd);
      setText(''); setPhoto(null); setRating(5);
      toast.success('Thanks for your review!');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not post review');
    } finally { setBusy(false); }
  };

  const avg = data.aggregate.average || 0;
  const count = data.aggregate.count || 0;
  const breakdown = data.aggregate.breakdown || {};
  const maxBucket = Math.max(1, ...Object.values(breakdown));

  return (
    <section data-testid={`reputation-${kind}-${targetId}`} className="card-soft p-5 mt-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">
            {title || 'Ratings & reviews'}
          </div>
          <h3 className="font-heading font-bold text-xl mt-1">
            {count === 0 ? 'No ratings yet' : (
              <span>
                <span className="text-3xl">{avg.toFixed(1)}</span>
                <span className="text-sm text-ink/60"> / 5</span>
                <span className="text-sm text-ink/50 ml-2">· {count} review{count !== 1 ? 's' : ''}</span>
              </span>
            )}
          </h3>
        </div>
        {count > 0 && (
          <div className="inline-flex items-center gap-0.5">
            {[1,2,3,4,5].map(n => (
              <Star key={n}
                className={`w-5 h-5 ${avg >= n - 0.25 ? 'fill-[#F1C40F] text-[#F1C40F]' : 'text-ink/20'}`}/>
            ))}
          </div>
        )}
      </div>

      {count > 0 && (
        <div className="mt-3 space-y-1">
          {[5,4,3,2,1].map(n => {
            const c = breakdown[String(n)] || 0;
            const pct = maxBucket ? (c / maxBucket) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs" data-testid={`rep-bar-${n}`}>
                <div className="w-6 tabular-nums text-right font-mono">{n}★</div>
                <div className="flex-1 h-2 rounded-full bg-[#F5EFE0] overflow-hidden">
                  <div className="h-full bg-[#F1C40F]" style={{ width: `${pct}%` }}/>
                </div>
                <div className="w-8 tabular-nums text-ink/60">{c}</div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && user && (
        <div className="mt-4 rounded-lg border border-border bg-white p-4">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-2">
            Your rating for {KIND_LABEL[kind] || 'this'}
          </div>
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => (
              <button key={n} data-testid={`rep-star-${n}`} onClick={() => setRating(n)} className="p-1">
                <Star className={`w-6 h-6 ${rating >= n ? 'fill-[#F1C40F] text-[#F1C40F]' : 'text-ink/30'}`}/>
              </button>
            ))}
          </div>
          <textarea data-testid="rep-comment" value={text} onChange={e => setText(e.target.value)}
            rows={2} placeholder="Share how this went — helps your neighbours decide."
            className="w-full mt-3 border border-border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20"/>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 pill !bg-white hover:border-[#E67E22] hover:text-[#D35400] cursor-pointer">
              <Camera className="w-3 h-3"/>{photo ? photo.name.slice(0, 24) : 'Attach a photo'}
              <input data-testid="rep-photo-input" type="file"
                accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                onChange={e => setPhoto(e.target.files?.[0] || null)}/>
            </label>
            {photo && (
              <button onClick={() => setPhoto(null)} className="text-xs text-ink/50 hover:text-ink/80 inline-flex items-center gap-1">
                <X className="w-3 h-3"/>Remove
              </button>
            )}
            <button data-testid="rep-submit" disabled={busy} onClick={submit} className="btn-primary ml-auto">
              {busy ? 'Posting…' : 'Post review'}
            </button>
          </div>
          {error && <div className="mt-2 text-xs text-[#C62828]">{error}</div>}
          <div className="text-[10px] text-ink/40 mt-2 uppercase tracking-wider">
            Only verified buyers with a delivered order can post. 7-day edit window. One review per {kind}.
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {data.reviews.length === 0 && count === 0
          ? <div className="text-sm text-ink/60">Be the first to review.</div>
          : data.reviews.map(r => <ReviewRow key={r.id} r={r} onChanged={load} me={user}/>)
        }
      </div>
    </section>
  );
}

function ReviewRow({ r, onChanged, me }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.text || '');
  const [rating, setRating] = useState(r.rating || 5);
  const [busy, setBusy] = useState(false);
  const isMine = me && (r.reviewer_id === me.id || r.reviewer_name === me.name);
  const created = new Date(r.created_at);
  const editableUntil = new Date(created.getTime() + 7 * 24 * 3600 * 1000);
  const canEdit = isMine && Date.now() < editableUntil.getTime();

  const save = async () => {
    try {
      setBusy(true);
      await reputation.edit(r.id, { rating, text });
      setEditing(false);
      toast.success('Review updated');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`rep-row-${r.id}`} className="rounded-lg border border-border bg-white p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm">{r.reviewer_name}</div>
          {r.is_local_guide && (
            <span data-testid={`rep-local-guide-${r.id}`}
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#E3F2FD] text-[#0D47A1] px-1.5 py-0.5 rounded-full">
              <Award className="w-2.5 h-2.5"/>Local Guide
            </span>
          )}
          {r.verified_buyer && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#E8F5E9] text-[#1B5E20] px-1.5 py-0.5 rounded-full">
              <ShieldCheck className="w-2.5 h-2.5"/>Verified
            </span>
          )}
        </div>
        <div className="inline-flex items-center gap-0.5">
          {[1,2,3,4,5].map(n => (
            <Star key={n}
              className={`w-3.5 h-3.5 ${(editing ? rating : r.rating) >= n ? 'fill-[#F1C40F] text-[#F1C40F]' : 'text-ink/20'}
                          ${editing ? 'cursor-pointer' : ''}`}
              onClick={() => editing && setRating(n)}/>
          ))}
        </div>
      </div>

      {editing
        ? (
          <div className="mt-2">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
              className="w-full border border-border bg-white rounded-lg px-3 py-2 text-sm"/>
            <div className="flex gap-2 mt-2">
              <button onClick={save} disabled={busy} className="btn-primary">Save</button>
              <button onClick={() => { setEditing(false); setText(r.text || ''); setRating(r.rating); }}
                className="pill !bg-white">Cancel</button>
            </div>
          </div>
        )
        : (
          <>
            {r.text && <div className="text-sm text-ink/80 mt-1.5">{r.text}</div>}
            {r.photo_url && (
              <a href={r.photo_url} target="_blank" rel="noreferrer" className="inline-block mt-2">
                <img data-testid={`rep-photo-${r.id}`} src={r.photo_url} alt="Review attachment"
                  className="max-h-40 rounded-lg border border-border object-cover"/>
              </a>
            )}
          </>
        )
      }

      <div className="mt-1 flex items-center justify-between">
        <div className="text-[10px] text-ink/40 uppercase tracking-wider inline-flex items-center gap-2">
          <MessageSquareText className="w-3 h-3"/>
          {created.toLocaleDateString()} {r.updated_at && r.updated_at !== r.created_at ? '· edited' : ''}
        </div>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)}
            className="text-xs text-[#D35400] hover:underline"
            data-testid={`rep-edit-${r.id}`}>Edit</button>
        )}
      </div>

      <MerchantResponse r={r} me={me} onChanged={onChanged}/>
    </div>
  );
}

function MerchantResponse({ r, me, onChanged }) {
  const [drafting, setDrafting] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const resp = r.merchant_response;
  const canRespond = me && !resp; // eligibility check enforced server-side

  const submit = async () => {
    if (!text.trim() || text.trim().length < 3) { toast.error('Write a few words to respond'); return; }
    try {
      setBusy(true);
      await reputation.respond(r.id, text.trim());
      toast.success('Response posted');
      setText(''); setDrafting(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post response');
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm('Remove your response?')) return;
    try { await reputation.deleteResponse(r.id); onChanged(); toast.success('Response removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  if (resp) {
    return (
      <div data-testid={`rep-response-${r.id}`}
        className="mt-3 ml-6 border-l-2 border-[#D35400] pl-3 py-2 bg-[#FFF8EE]/60 rounded-r-md">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#D35400]">
          Response from {resp.responder_role === 'merchant' ? 'the shop' : resp.responder_role} · {resp.responder_name}
        </div>
        <div className="text-sm text-ink/80 mt-1">{resp.text}</div>
        <div className="mt-1 flex items-center justify-between">
          <div className="text-[10px] text-ink/40 uppercase tracking-wider">
            {new Date(resp.created_at).toLocaleDateString()}
          </div>
          {me && resp.responder_id === me.id && (
            <button onClick={del} className="text-xs text-[#C62828] hover:underline"
              data-testid={`rep-response-delete-${r.id}`}>Remove</button>
          )}
        </div>
      </div>
    );
  }
  if (!canRespond) return null;
  return (
    <div className="mt-3">
      {!drafting
        ? <button onClick={() => setDrafting(true)}
            data-testid={`rep-respond-btn-${r.id}`}
            className="text-xs font-semibold text-[#D35400] hover:underline">
            Respond as the shop (owner-only)
          </button>
        : (
          <div className="mt-1 rounded-md border border-[#E67E22]/30 bg-[#FFF8EE] p-2">
            <textarea data-testid={`rep-respond-input-${r.id}`}
              value={text} onChange={e => setText(e.target.value)} rows={2}
              placeholder="Give context or acknowledge — keep it kind."
              className="w-full text-sm border border-border rounded-md px-2 py-1"/>
            <div className="mt-1 flex justify-end gap-2">
              <button onClick={() => { setDrafting(false); setText(''); }}
                className="text-xs text-ink/60 hover:underline">Cancel</button>
              <button onClick={submit} disabled={busy}
                data-testid={`rep-respond-submit-${r.id}`}
                className="text-xs font-semibold text-[#D35400] hover:underline">
                {busy ? 'Posting…' : 'Post response'}
              </button>
            </div>
          </div>
        )
      }
    </div>
  );
}
